import React from "react";

import { withFirebase } from "../Firebase/FirebaseContext";
import { withRefreshUsers } from "../Firebase/RefreshUsersContext";
import AuthUserContext from "./AuthUserContext";
import UserContext from "./UserContext";

import cloudinary from "../../utils/cloudinary";
import * as crisp from "../../utils/crisp";

type WithAuthenticationProps = {
  firebase: any;
  refreshUsers: () => void;
};

type WithAuthenticationState = {
  initFirebase: boolean;
  authUser: any;
  user: any;
};

const withAuthentication = (Component: React.ComponentType<any>) => {
  class WithAuthentication extends React.Component<
    WithAuthenticationProps,
    WithAuthenticationState
  > {
    constructor(props: WithAuthenticationProps) {
      super(props);

      this.state = {
        initFirebase: false,
        authUser: null,
        user: null,
      };
    }

    /**
     * A changed email only takes effect in Firebase Auth once the user opens
     * the verification link, and can be reverted from the notice sent to the
     * old address. This syncs the user document with it upon login.
     */
    syncEmails = async (authUser: any) => {
      const document = await this.props.firebase.onceGetUser(authUser.uid);
      const user = document.data();
      if (user.email !== authUser.email) {
        console.warn("Email out of sync, updating...");
        this.props.firebase.doUpdateUser(authUser.uid, {
          email: authUser.email,
        });
      }
    };

    firebaseInit = () => {
      if (this.props.firebase && !this.state.initFirebase) {
        this.props.firebase.auth.onAuthStateChanged((authUser: any) => {
          if (authUser) {
            this.setState(() => ({ authUser, initFirebase: true }));

            this.syncEmails(authUser);

            this.props.firebase.analytics.setUserId(authUser.uid);
            crisp.setCrispUserField(`email`, authUser.email);

            this.props.firebase.onUserUpdate(
              authUser.uid,
              (snapshot: any) => {
                const user = snapshot.data();
                this.setState({
                  user: Object.assign({ id: snapshot.id }, user),
                });

                if (user && user.firstName && user.lastName)
                  crisp.setCrispUserField(
                    `nickname`,
                    `${user.firstName} ${user.lastName}`,
                  );
                if (user && user.profilePicture)
                  crisp.setCrispUserField(
                    `avatar`,
                    `${cloudinary.BASE_URL}w_200,h_200,c_fill,q_auto:good,f_auto/v1/${user.profilePicture.publicId}`,
                  );
              },
            );

            this.props.firebase.doUpdateLastSignInAt(
              authUser.uid,
              authUser.metadata.creationTime,
              authUser.metadata.lastSignInTime,
            );

            // let logged in users get the real time user list
            this.props.refreshUsers();
          } else {
            this.setState(() => ({
              authUser: null,
              initFirebase: true,
            }));
          }
        });
      }
    };

    componentDidMount() {
      this.firebaseInit();
    }

    componentDidUpdate() {
      this.firebaseInit();
    }

    render() {
      const { authUser, user } = this.state;

      return (
        <AuthUserContext.Provider value={authUser}>
          <UserContext.Provider value={user}>
            <Component {...this.props} />
          </UserContext.Provider>
        </AuthUserContext.Provider>
      );
    }
  }

  return withFirebase(withRefreshUsers(WithAuthentication));
};

export default withAuthentication;
